import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CardHeader from "../components/CardHeader";
import PageHeader from "../../../../core/common/crmComponents/PageHeader";
import PaginationControls from "../../../../core/common/pagination";
import LoaderComponent from "../../../../core/common/crmComponents/LoaderComponent";
import DeleteDeal from "../actions/DeleteDeal";
import DealList from "../components/DealList";
import DealGridLayout from "../components/DealGridLayout";
import { useDealsData } from "../hooks/useDealsData";
import { MODULES } from "../../../../core/data/constants/moduleConstants";
import { useSearchParams } from "react-router-dom";
import { ACCOUNTANT } from "../../../../core/data/constants/constant";
import {
  getUrlParams,
  setUrlParams,
} from "../../../../utils/helpers/setAndGetUrlParams";
import { generateQueryParams } from "../../../../utils/helpers/generateQueryParams";
import ZoomStatusLegend from "../../../../core/common/crmComponents/zoomMeeting/ZoomStatusLegend";
import { useFunctionalityAccess } from "../../../../core/common/wrapper/HasFunctionalityAccess";
import {
  resetRefetchFlag,
  setReloading,
} from "../../../../core/data/redux/moduleDataSlice";
import queryClient from "../../../../config/queryClient";

const Deals = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const refetchDeals = useSelector(
    (state) => state.moduleData.refetchFlags.deals
  );

  const { user } = useSelector((state) => state?.auth);
  const isUserAccountant = user?.role?.name === ACCOUNTANT;
  const { companyNameArr } = useSelector((state) => state.auth);
  const hasZoomAccess = useFunctionalityAccess("zoomSetting");
  const reloading = useSelector((state) => state.moduleData.reloading);

  const [layout, setLayout] = useState("list");
  const initialLimit = getUrlParams("limit")
    ? Math.ceil(getUrlParams("limit"))
    : 50;
  const initialPage = getUrlParams("page") ? Number(getUrlParams("page")) : 1;
  const initialCompany = getUrlParams("companyid")
    ? getUrlParams("companyid")
    : user?.ofCompany?._id ||
      (companyNameArr.length > 0 ? companyNameArr[0].value : null);

  const [limit, setLimit] = useState(initialLimit);
  const [page, setPage] = useState(initialPage);
  const [selectedCompany, setSelectedCompany] = useState(initialCompany);

  const toggleLayout = () => {
    setLayout((prevLayout) => (prevLayout === "list" ? "grid" : "list"));
  };

  const { filters, hasNewFilters } = useSelector((state) => state.filter);
  const dealsFilters = filters.deals || [];

  const { queryParams, params } = generateQueryParams(dealsFilters);

  const { data, isLoading, isError, error, refetch, isFetching } = useDealsData(
    {
      page: page ? page : 1,
      limit: limit ? limit : 50,
      ofCompany: selectedCompany ? selectedCompany : null,
      queryString: params.toString(),
      queryParams: queryParams.toString(),
    }
  );

  const dealsData = data?.data?.data;

  const totalCount = data?.data?.totalResult;
  const totalPages = Math.ceil(totalCount / limit);

  useEffect(() => {
    if (refetchDeals) {
      refetch(); // This triggers the TanStack Query to refetch the data
      dispatch(resetRefetchFlag("deals")); // Optional: reset the flag so it doesn't keep refetching
    }
  }, [refetchDeals, refetch, dispatch]);

  useEffect(() => {
    if (!searchParams.get("companyid")) {
      setUrlParams(setSearchParams, "companyid", initialCompany);
    }
    setSelectedCompany(initialCompany);
  }, [initialCompany]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages); // Reset to the last available page
      setUrlParams(setSearchParams, "page", totalPages);
    }
  }, [totalPages, page, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("page") !== String(page)) {
      setUrlParams(setSearchParams, "page", page);
    }
  }, [page]);

  useEffect(() => {
    if (selectedCompany) {
      setUrlParams(setSearchParams, "companyid", selectedCompany);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (
      searchParams.get("page") ||
      searchParams.get("limit") !== String(limit)
    ) {
      setUrlParams(setSearchParams, "limit", limit);
    }
  }, [limit]);

  const handlePageChange = (newPage) => {
    setPage(newPage); // Update page state to trigger refetch
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1); // Reset page to 1 whenever limit changes
  };

  useEffect(() => {
    if (dealsFilters?.length > 0 && hasNewFilters) {
      setPage(1);
    } else {
      setPage(page);
      setLimit(limit);
    }
  }, [dealsFilters]);

  const filteredDealsData = isUserAccountant
    ? dealsData?.filter((deal) => deal?.dealSentForApprovalToAccountant)
    : dealsData;

  const filteredDealsDataCount = isUserAccountant
    ? filteredDealsData?.length
    : data?.data?.totalResult;

  useEffect(() => {
    if (reloading) {
      queryClient.invalidateQueries(["deals"]);
      dispatch(setReloading(false));
    }
  }, [reloading, dispatch]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content custom-content-padding">
          <div className="row">
            <div className="col-md-12">
              <PageHeader
                pageName="Deals"
                totalCount={filteredDealsDataCount || 0}
                {...(hasZoomAccess && {
                  children2: <ZoomStatusLegend />,
                })}
              />
              <div className="card">
                <CardHeader
                  layout={layout}
                  toggleLayout={toggleLayout}
                  selectedCompany={selectedCompany}
                  setSelectedCompany={setSelectedCompany}
                />
                <div className="card-body py-2">
                  <PaginationControls
                    totalPages={totalPages}
                    totalCount={filteredDealsDataCount}
                    currentPageDataCount={Math.min(
                      filteredDealsData?.length,
                      limit
                    )}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    moduleName={MODULES.DEALS}
                  />

                  {isLoading ? (
                    <LoaderComponent />
                  ) : isError ? (
                    <div>Error: {error.message}</div>
                  ) : layout === "list" ? (
                    <DealList dealsData={filteredDealsData} />
                  ) : (
                    <DealGridLayout dealsData={filteredDealsData} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteDeal />
    </>
  );
};

export default Deals;
